(() => {
  const FREE_SHIPPING_THRESHOLD_CENTS = 8900;
  const FREE_SHIPPING_REMINDER_WINDOW_CENTS = 1000;

  const getFreeShippingThreshold = () => {
    const promoBlock = document.querySelector('[data-lm-cart-promos][data-free-shipping-threshold]');
    return Number(promoBlock?.dataset.freeShippingThreshold || FREE_SHIPPING_THRESHOLD_CENTS);
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Math.max(cents, 0) / 100);
  };

  const getCart = async () => {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };

  const unitItems = (cart) =>
    cart.items.flatMap((item) =>
      Array.from({ length: item.quantity }, () => ({
        title: item.product_title || item.title,
        variant: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        image: item.image,
        url: item.url,
        handle: item.handle || '',
        price: item.final_price,
      })),
    );

  const isDiscountExcludedItem = (item) => {
    const title = String(item.title || '').toLowerCase();
    const handle = String(item.handle || '').toLowerCase();

    return title.includes('excludes sitewide discounts') || handle.includes('excludes-sitewide-discounts');
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };
      return entities[character];
    });

  const parseJsonSetting = (value, fallback) => {
    if (!value) return fallback;

    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
    } catch {
      return fallback;
    }
  };

  const defaultBundleRules = [
    { quantity: 4, free_items: 1, label: 'Buy 3, Get 1' },
    { quantity: 10, free_items: 3, label: 'Buy 7, Get 3' },
  ];

  const normalizeBundleRules = (rules) =>
    (Array.isArray(rules) ? rules : [])
      .map((rule) => ({
        quantity: Number(rule.quantity || rule.minimum_quantity || 0),
        freeItems: Number(rule.free_items || rule.freeItems || rule.gift_count || 0),
        label: String(rule.label || rule.title || ''),
      }))
      .filter((rule) => rule.quantity > 0 && rule.freeItems > 0)
      .sort((a, b) => a.quantity - b.quantity);

  const getBundleRules = (block) => {
    const rules = normalizeBundleRules(parseJsonSetting(block.dataset.bundleRules, defaultBundleRules));
    return rules.length ? rules : normalizeBundleRules(defaultBundleRules);
  };

  const getBestBundleRule = (rules, quantity) =>
    [...rules].reverse().find((rule) => quantity >= rule.quantity) || null;

  const getNextBundleRule = (rules, quantity) => rules.find((rule) => quantity < rule.quantity) || null;

  const getBundleLabel = (rule) => rule.label || `Buy ${rule.quantity - rule.freeItems}, Get ${rule.freeItems}`;

  const replaceTemplateTokens = (template, tokens) =>
    Object.entries(tokens).reduce((message, [token, value]) => message.split(`[${token}]`).join(escapeHtml(value)), String(template || ''));

  const bundleExclusionNotice = (excludedCount, block) =>
    excludedCount > 0
      ? escapeHtml(
          block?.dataset.bundleExclusionNotice ||
            'PLA Filament 1KG is excluded from all discounts. Orders using incorrect discounts for excluded items may be canceled and refunded.',
        )
      : '';

  const bundleMessageForQuantity = (rules, quantity, excludedCount = 0, block = null) => {
    const exclusionNotice = bundleExclusionNotice(excludedCount, block);
    if (quantity === 0) return exclusionNotice;

    const bestRule = getBestBundleRule(rules, quantity);
    if (bestRule) {
      const freeItemText = bestRule.freeItems === 1 ? 'lowest-priced item' : `${bestRule.freeItems} lowest-priced items`;
      const message = replaceTemplateTokens(
        block?.dataset.bundleQualifiedText ||
          'You qualify for [deal] savings. Shopify checkout should make the [free_item_text] free when the automatic discount is active.',
        {
          deal: getBundleLabel(bestRule),
          free_item_text: freeItemText,
        },
      );
      return [message, exclusionNotice].filter(Boolean).join(' ');
    }

    const nextRule = getNextBundleRule(rules, quantity);
    if (!nextRule) return exclusionNotice;

    const remaining = nextRule.quantity - quantity;
    const message = replaceTemplateTokens(block?.dataset.bundleProgressText || 'Add [remaining] more qualifying filament [item_word] to reach [deal] savings.', {
      remaining,
      item_word: remaining === 1 ? 'item' : 'items',
      deal: getBundleLabel(nextRule),
    });
    return [message, exclusionNotice].filter(Boolean).join(' ');
  };

  const isCartPromoDismissed = (cart) =>
    sessionStorage.getItem('lm-cart-promos-dismissed') === 'true' ||
    (cart?.token && sessionStorage.getItem(`lm-cart-promos-dismissed-${cart.token}`) === 'true');

  const hideCartPromoBlock = (block) => {
    block.hidden = true;
    block.classList.add('is-dismissed');
    block.style.display = 'none';
  };

  const showCartPromoBlock = (block) => {
    block.hidden = false;
    block.classList.remove('is-dismissed');
    block.style.removeProperty('display');
  };

  const syncPromoBlockVisibility = (block) => {
    const hasVisibleMessage = [...block.querySelectorAll('[data-lm-free-shipping-message], [data-lm-bundle-message], [data-lm-gift-preview]')].some(
      (message) => !message.hidden,
    );

    if (hasVisibleMessage) {
      showCartPromoBlock(block);
    } else {
      hideCartPromoBlock(block);
    }
  };

  const syncCartPromos = async () => {
    const promoBlocks = [...document.querySelectorAll('[data-lm-cart-promos]')];
    if (!promoBlocks.length) return;

    let cart;
    try {
      cart = await getCart();
    } catch {
      return;
    }

    const units = unitItems(cart);
    const eligibleUnits = units.filter((item) => !isDiscountExcludedItem(item)).sort((a, b) => a.price - b.price);
    const excludedItemCount = units.length - eligibleUnits.length;
    const itemCount = eligibleUnits.length;

    promoBlocks.forEach((block) => {
      block.dataset.dismissKey = cart.token ? `lm-cart-promos-dismissed-${cart.token}` : 'lm-cart-promos-dismissed';

      if (isCartPromoDismissed(cart)) {
        hideCartPromoBlock(block);
        return;
      }

      const threshold = Number(block.dataset.freeShippingThreshold || FREE_SHIPPING_THRESHOLD_CENTS);
      const windowCents = Number(block.dataset.freeShippingWindow || FREE_SHIPPING_REMINDER_WINDOW_CENTS);
      const shopUrl = block.dataset.shopUrl || '/collections/all';
      const shippingMessage = block.querySelector('[data-lm-free-shipping-message]');
      const bundleMessage = block.querySelector('[data-lm-bundle-message]');
      const giftPreview = block.querySelector('[data-lm-gift-preview]');
      const bundleRules = getBundleRules(block);

      if (shippingMessage) {
        const amountAway = threshold - cart.total_price;
        if (amountAway > 0 && amountAway <= windowCents) {
          shippingMessage.hidden = false;
          shippingMessage.innerHTML = `<strong>${formatMoney(amountAway)} away from free shipping.</strong><span>Add filament now to unlock free shipping over ${formatMoney(threshold)}.</span><a href="${shopUrl}">Keep shopping</a>`;
        } else if (cart.total_price >= threshold) {
          shippingMessage.hidden = false;
          shippingMessage.innerHTML = `<strong>Free shipping unlocked.</strong><span>Your cart is over ${formatMoney(threshold)}.</span>`;
        } else {
          shippingMessage.hidden = true;
          shippingMessage.innerHTML = '';
        }
      }

      if (bundleMessage) {
        const message = bundleMessageForQuantity(bundleRules, itemCount, excludedItemCount, block);
        const heading = escapeHtml(block.dataset.bundleHeading || 'Bundle deal');
        const ctaLabel = escapeHtml(block.dataset.bundleCtaLabel || 'Add items');

        bundleMessage.hidden = !message;
        bundleMessage.innerHTML = message ? `<strong>${heading}</strong><span>${message}</span><a href="${shopUrl}">${ctaLabel}</a>` : '';
      }

      if (giftPreview) {
        const giftCount = getBestBundleRule(bundleRules, itemCount)?.freeItems || 0;
        const gifts = eligibleUnits.slice(0, giftCount);

        giftPreview.hidden = gifts.length === 0;
        giftPreview.innerHTML = gifts.length
          ? `<p>Estimated $0 bundle item${gifts.length > 1 ? 's' : ''}</p>${gifts
              .map(
                (gift) => `<a href="${gift.url}">${gift.image ? `<img src="${gift.image}" alt="">` : ''}<span><strong>${gift.title}</strong>${gift.variant ? `<small>${gift.variant}</small>` : ''}</span><b>$0</b></a>`,
              )
              .join('')}`
          : '';
      }

      syncPromoBlockVisibility(block);
    });

    const globalFreeShippingThreshold = getFreeShippingThreshold();
    const amountAway = globalFreeShippingThreshold - cart.total_price;
    if (
      amountAway > 0 &&
      amountAway <= FREE_SHIPPING_REMINDER_WINDOW_CENTS &&
      !sessionStorage.getItem(`lm-free-shipping-${cart.token}`)
    ) {
      sessionStorage.setItem(`lm-free-shipping-${cart.token}`, 'shown');
      const toast = document.createElement('div');
      toast.className = 'lm-cart-toast';
      toast.innerHTML = `<button type="button" aria-label="Close">&times;</button><strong>${formatMoney(amountAway)} away from free shipping</strong><span>Add another spool or value pack to unlock free shipping over ${formatMoney(globalFreeShippingThreshold)}.</span><a href="/collections/all">Shop filaments</a>`;
      toast.querySelector('button')?.addEventListener('click', () => toast.remove());
      document.body.append(toast);
      window.setTimeout(() => toast.remove(), 9000);
    }
  };

  const setupNewsletterPopup = () => {
    const popup = document.querySelector('[data-lm-newsletter-popup]');
    if (!popup || localStorage.getItem('lm-newsletter-popup-closed') === 'true') return;

    const close = () => {
      popup.hidden = true;
      localStorage.setItem('lm-newsletter-popup-closed', 'true');
    };

    popup.querySelectorAll('[data-lm-newsletter-close]').forEach((button) => button.addEventListener('click', close));
    popup.querySelector('[data-lm-newsletter-submit]')?.addEventListener('click', () => {
      localStorage.setItem('lm-new-subscriber-credit', 'true');
    });

    const delay = Number(popup.dataset.lmNewsletterDelay || 1250);
    window.setTimeout(() => {
      popup.hidden = false;
    }, Number.isFinite(delay) ? delay : 1250);
  };

  const setupCountdowns = () => {
    const countdowns = [...document.querySelectorAll('[data-lm-countdown]')];
    if (!countdowns.length) return;

    const tick = () => {
      countdowns.forEach((countdown) => {
        const output = countdown.querySelector('[data-lm-countdown-output]');
        const target = new Date(countdown.dataset.countdownTarget || '').getTime();
        const diff = target - Date.now();

        if (!output || Number.isNaN(target)) return;
        if (diff <= 0) {
          output.textContent = 'Live now';
          return;
        }

        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        output.textContent = `${days}d ${hours}h ${minutes}m`;
      });
    };

    tick();
    window.setInterval(tick, 60000);
  };

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('button[name="checkout"], #checkout, #CartDrawer-Checkout');

    if (checkoutButton && localStorage.getItem('lm-new-subscriber-credit') === 'true') {
      event.preventDefault();
      window.location.href = '/discount/WELCOME10?redirect=/checkout';
    }
  });

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-lm-discount-form]');
    const code = discountForm?.querySelector('input[name="discount"]')?.value.trim();

    if (discountForm && code) {
      event.preventDefault();
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => window.setTimeout(syncCartPromos, 250));
  }

  document.addEventListener('cart:updated', syncCartPromos);
  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-lm-cart-promos-close]');
    if (!closeButton) return;

    const block = closeButton.closest('[data-lm-cart-promos]');
    if (!block) return;

    sessionStorage.setItem(block.dataset.dismissKey || 'lm-cart-promos-dismissed', 'true');
    hideCartPromoBlock(block);
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('cart-items, cart-drawer-items, [data-lm-cart-promos]')) {
      window.setTimeout(syncCartPromos, 700);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-lm-compare]').forEach((compare) => {
      const sync = () => {
        const checked = [...compare.querySelectorAll('[data-lm-compare-toggle]:checked')].map(
          (input) => input.dataset.lmCompareToggle,
        );
        const active = checked.length ? checked : ['0'];

        compare.querySelectorAll('[data-lm-compare-col]').forEach((cell) => {
          cell.hidden = !active.includes(cell.dataset.lmCompareCol);
        });
      };

      compare.querySelectorAll('[data-lm-compare-toggle]').forEach((input) => {
        input.addEventListener('change', sync);
      });

      sync();
    });

    setupNewsletterPopup();
    setupCountdowns();
    syncCartPromos();
  });
})();
